import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import {
  CompanyConfig,
  CompanyId,
  COMPANIES,
  resolveCurrentCompanyId,
  applyCompanyMetadata,
} from '../Config/CompanyConfig';
import { getCompanyTheme } from './Theme';

interface CompanyContextType {
  company: CompanyConfig;
  companyId: CompanyId;
  isEKEDP: boolean;
  isEkoPower: boolean;
  setCompany: (id: CompanyId) => void;
  toggleCompany: () => void;
}

const CompanyContext = createContext<CompanyContextType>({
  company: COMPANIES.ekopower,
  companyId: 'ekopower',
  isEKEDP: false,
  isEkoPower: true,
  setCompany: () => {},
  toggleCompany: () => {},
});

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companyId, setCompanyIdState] = useState<CompanyId>(() => resolveCurrentCompanyId());

  const company = useMemo(() => COMPANIES[companyId] || COMPANIES.ekopower, [companyId]);
  const activeTheme = useMemo(() => getCompanyTheme(companyId), [companyId]);

  // Apply favicon, document title, and meta tags dynamically
  useEffect(() => {
    applyCompanyMetadata(company);
  }, [company]);

  // Proactively clear any stale dev override if loading on authoritative corporate domains
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname.toLowerCase();
      if (host.includes('ekedp') || host.includes('ekedc') || host.includes('ekopower')) {
        try {
          localStorage.removeItem('app-company-override');
        } catch (e) {}
      }
    }
  }, []);

  const setCompany = (id: CompanyId) => {
    if (COMPANIES[id]) {
      setCompanyIdState(id);
      try {
        const host = window.location.hostname.toLowerCase();
        if (!host.includes('ekedp') && !host.includes('ekedc') && !host.includes('ekopower')) {
          localStorage.setItem('app-company-override', id);
        }
      } catch (e) {
        // Ignore localStorage error if disabled
      }
    }
  };

  const toggleCompany = () => {
    const nextId: CompanyId = companyId === 'ekopower' ? 'ekedp' : 'ekopower';
    setCompany(nextId);
  };

  const contextValue = useMemo<CompanyContextType>(
    () => ({
      company,
      companyId,
      isEKEDP: companyId === 'ekedp',
      isEkoPower: companyId === 'ekopower',
      setCompany,
      toggleCompany,
    }),
    [company, companyId]
  );

  return (
    <CompanyContext.Provider value={contextValue}>
      <ColorModeScript initialColorMode={activeTheme.config.initialColorMode} />
      <ChakraProvider theme={activeTheme}>
        {children}
      </ChakraProvider>
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export default CompanyContext;
